import React, { useState } from "react";
import { Box, Container, Grid, Stack, Typography, Dialog, IconButton } from "@mui/material";
import { BsShieldCheck, BsClock, BsCheckCircleFill } from "react-icons/bs";
import { RxCross2 } from "react-icons/rx";
import count_service_img from "../../assets/count_service_img.webp";

const ACCENT = "#FF6B3D";
const ACCENT_SOFT = "#FFF1EA";
const INK = "#0F2231";
const MUTED = "#5A6772";

const AboutSection = () => {
  const [guaranteeOpen, setGuaranteeOpen] = useState(false);

  const features = [
    { icon: <BsClock size={18} />, text: "5 min average response", highlight: true },
  ];

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        py: { xs: 4, md: 6 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 10 }} alignItems="center">
          {/* Image Side */}
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Box
              sx={{
                position: "relative",
                width: { xs: "90%", md: "85%" },
                margin: "0 auto",
                borderRadius: "28px",
                overflow: "hidden",
                boxShadow: "0 30px 60px -20px rgba(15, 34, 49, 0.18), 0 12px 24px -12px rgba(15, 34, 49, 0.08)",
              }}
            >
              <img
                src={count_service_img}
                alt="Professional Service"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </Box>
          </Grid>

          {/* Content Side */}
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Stack spacing={3.5}>
              <Typography
                sx={{
                  fontSize: { xs: "32px", sm: "38px", md: "48px" },
                  fontWeight: 700,
                  color: INK,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                We've Got You Covered
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "15px", md: "17px" },
                  color: MUTED,
                  lineHeight: 1.65,
                  maxWidth: "480px",
                }}
              >
                Vetted, background-checked, and rated on every job. Not a perfect experience? Get a re-do or a full refund.
              </Typography>

              <Stack spacing={1.5} sx={{ pt: 1 }}>
                {features.map((feature, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: "14px",
                      background: feature.highlight ? ACCENT_SOFT : "#FFFFFF",
                      border: feature.highlight ? `1px solid ${ACCENT}20` : "1px solid #EEF0F2",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 8px 20px -8px rgba(15, 34, 49, 0.08)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        background: feature.highlight ? ACCENT : ACCENT_SOFT,
                        color: feature.highlight ? "#FFFFFF" : ACCENT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: "15px", md: "16px" },
                        color: INK,
                        fontWeight: feature.highlight ? 600 : 500,
                      }}
                    >
                      {feature.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pt: 1 }}>
                <BsShieldCheck size={18} color={ACCENT} style={{ flexShrink: 0 }} />
                <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, color: MUTED }}>
                  Backed by our satisfaction guarantee.
                </Typography>
                <Typography
                  onClick={() => setGuaranteeOpen(true)}
                  sx={{
                    fontSize: { xs: "13px", md: "14px" },
                    color: ACCENT,
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Learn more
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Guarantee Popup */}
      <Dialog
        open={guaranteeOpen}
        onClose={() => setGuaranteeOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxWidth: "460px",
            width: "90%",
            p: 0,
            overflow: "hidden",
            boxShadow: "0 40px 80px -20px rgba(15, 34, 49, 0.3)",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Box sx={{ background: `linear-gradient(135deg, ${INK} 0%, #1a3548 100%)`, p: 3.5, textAlign: "center" }}>
            <IconButton
              onClick={() => setGuaranteeOpen(false)}
              sx={{ position: "absolute", top: 12, right: 12, color: "#fff" }}
            >
              <RxCross2 size={18} />
            </IconButton>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "16px",
                background: "rgba(255,255,255,0.1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <BsShieldCheck size={28} color={ACCENT} />
            </Box>
            <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#fff" }}>
              Satisfaction Guarantee
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.65)", mt: 0.5 }}>
              Your peace of mind is our priority
            </Typography>
          </Box>

          <Stack spacing={2.5} sx={{ p: 3.5 }}>
            {[
              { title: "Quality Assurance", desc: "Every professional is licensed, insured, and background-checked before joining our platform." },
              { title: "Money-Back Promise", desc: "If you're not satisfied with the work, we'll send another pro or refund your payment — no questions asked." },
              { title: "24/7 Support", desc: "Our support team is available around the clock to resolve any issues quickly." },
            ].map((item, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                <BsCheckCircleFill size={16} color={ACCENT} style={{ flexShrink: 0, marginTop: 3 }} />
                <Stack spacing={0.3}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: INK }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: MUTED, lineHeight: 1.55 }}>
                    {item.desc}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AboutSection;
